/**
 * Production HTTP client for Advokat platform
 */
const BASE_URL = '/api';

export async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem('advokat_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  // If body is FormData, do not set Content-Type header
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  let response;
  try {
    response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      signal: options.signal || controller.signal,
      headers,
      body: options.body instanceof FormData 
        ? options.body 
        : options.body ? JSON.stringify(options.body) : undefined,
    });
  } catch (netErr) {
    clearTimeout(timeoutId);
    if (netErr.name === 'AbortError') {
      throw new Error('Server javob berish vaqti tugadi (Timeout: 15s)');
    }
    throw netErr;
  }
  clearTimeout(timeoutId);

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    if ([502, 503, 504, 530].includes(response.status) && !options._retried) {
      // Automatic 1-time retry for transient Cloudflare tunnel disconnects
      await new Promise((r) => setTimeout(r, 1200));
      return apiRequest(endpoint, { ...options, _retried: true });
    }

    let errorMsg = data?.message || data?.error;
    if (!errorMsg) {
      if (response.status === 530) {
        errorMsg = 'Tarmoq qayta ulanmoqda (Cloudflare 530). Iltimos, 2-3 soniya kuting va qayta urinib ko‘ring.';
      } else if (response.status === 502 || response.status === 503) {
        errorMsg = 'Server vaqtincha band yoki qayta ishga tushmoqda. Qayta urinib ko‘ring.';
      } else {
        errorMsg = `So‘rov xatosi (${response.status})`;
      }
    }

    const error = new Error(errorMsg);
    error.status = response.status;
    error.data = data;

    if ((response.status === 401 || (response.status === 403 && data?.error === 'SubscriptionRevoked')) && !endpoint.includes('/auth/login')) {
      window.dispatchEvent(new CustomEvent('advokat:unauthorized', { detail: { message: errorMsg } }));
    }

    throw error;
  }

  return data;
}

export const api = {
  get: (url, options) => apiRequest(url, { method: 'GET', ...options }),
  post: (url, body, options) => apiRequest(url, { method: 'POST', body, ...options }),
  postFormData: (url, formData, options) => apiRequest(url, { method: 'POST', body: formData, ...options }),
  put: (url, body, options) => apiRequest(url, { method: 'PUT', body, ...options }),
  delete: (url, options) => apiRequest(url, { method: 'DELETE', ...options }),
};
